"use client";

import { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";

export default function VideoBackground() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [blurIntensity, setBlurIntensity] = useState(0);

  const handleLoadedData = () => {
    // Video metadata loaded, duration is available
    if (videoRef.current) {
      // Start video at beginning when loaded
      videoRef.current.currentTime = 0;
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      const currentTime = videoRef.current.currentTime;
      const duration = videoRef.current.duration;

      // Start blurring in the last 2 seconds of the video
      const blurStartTime = duration - 2;
      if (currentTime >= blurStartTime) {
        const progress = (currentTime - blurStartTime) / 2; // 0 to 1 over 2 seconds
        setBlurIntensity(progress * 4); // Max blur of 4px
      } else {
        setBlurIntensity(0);
      }
    }
  };

  const handleEnded = () => {};

  useEffect(() => {
    const handleResize = () => {
      const video = videoRef.current;
      if (!video) return;

      const isDesktop = window.innerWidth >= 900;
      const currentSrc = video.currentSrc;

      // Check if the current playing file matches the screen size
      if (isDesktop && !currentSrc.includes("16_9.mp4")) {
        reloadVideo();
      } else if (!isDesktop && currentSrc.includes("16_9.mp4")) {
        reloadVideo();
      }
    };

    const reloadVideo = () => {
      if (!videoRef.current) return;
      videoRef.current.load(); // Forces the browser to re-evaluate the <source> tags
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <Box
      sx={{
        bgcolor: "#22435c",
        position: "relative",
        width: "100%",
        height: "100%",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: "100%",
          overflow: "hidden",
          backgroundImage: {
            xs: "url(/branding/child_music_note.webp)",
            md: "url(/branding/child_music_note_16_9.webp)",
          },
          backgroundSize: "cover",
          opacity: 0,
          animation: "fadeIn 2s ease-in forwards",
          "@keyframes fadeIn": {
            "0%": {
              opacity: 0,
            },
            "100%": {
              opacity: 1,
            },
          },
        }}
      >
        <video
          ref={videoRef}
          muted
          playsInline
          autoPlay
          onLoadedMetadata={handleLoadedData}
          onTimeUpdate={handleTimeUpdate}
          onEnded={handleEnded}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            filter: `blur(${blurIntensity}px)`,
            transition: "filter 0.1s ease-out",
          }}
        >
          Your browser does not support the video tag.
          <source
            src="/branding/child_music_note_16_9.mp4"
            media="(min-width: 900px)"
          />
          <source
            src="/branding/child_music_note.mp4"
            media="(max-width: 899px)"
          />
        </video>
      </Box>
    </Box>
  );
}
